import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Tooltip, Spinner } from "@tag/tag-components-react-v4";
import CustomerService from "../../services/CustomerService";
import { CustomerDTO } from "../../types/CustomerDTO";
import { CareRosteringEnableInfoModel } from "../../types/CareRosteringEnableInfoModel";
import { ENABLE_CARE_ROSTERING_TOOLTIP_TEXT } from "../../common/constant";
import { ModuleAlert } from "../../common/ModuleAlert/ModuleAlert";

import styles from "./EnableCareRosteringModal.module.scss";
import { ProjectAccents } from "../../../General/config/projectAccents";


interface EnableCareRosteringModalProps {
  visible: boolean;
  selectedCustomer: CustomerDTO;
  handleClose: Function;
  setAlert: Function;
  handleCustomerMenuModalClose: Function;
  reloadCustomerData?: Function;
  updateCustomerCareRosteringStatus?: (clientId: number, isEnabled: boolean) => Promise<void>;
}

export const EnableCareRosteringModal: React.FC<
  EnableCareRosteringModalProps
> = ({ visible, selectedCustomer, handleClose, setAlert, handleCustomerMenuModalClose, reloadCustomerData, updateCustomerCareRosteringStatus }) => {
  const [isCareRosteringEnabled, setIsCareRosteringEnabled] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [alert, setAlertState] = useState<{ message: string; type: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const enableInfo: CareRosteringEnableInfoModel = {
        enableCareRostering: isCareRosteringEnabled,
      };

      await CustomerService.updateCareRosteringEnableInfo(
        selectedCustomer.clientId,
        enableInfo,
        selectedCustomer.environmentName
      );
      setAlert({type: 'success', message: 'Settings saved successfully'});
      
      // Use the targeted update function if available
      if (updateCustomerCareRosteringStatus) {
        await updateCustomerCareRosteringStatus(selectedCustomer.clientId, isCareRosteringEnabled);
      } else if (reloadCustomerData) {
        // Fall back to reloading all customers if the targeted update is not available
        reloadCustomerData();
      }
      
      handleClose();
      handleCustomerMenuModalClose();
    } catch (error) {
      setAlert({type: 'error', message: 'Failed to save settings. Please try again'});
      handleClose();
      handleCustomerMenuModalClose();
      console.error("Error updating Care Rostering settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchCareRosteringInfo = async () => {
      try {
        setLoading(true);
        const data = await CustomerService.getCareRosteringEnableInfo(selectedCustomer.clientId, selectedCustomer.environmentName);
        
        if (isMounted) {
          setIsCareRosteringEnabled(data.enableCareRostering);
          setIsDirty(false);
        }
      } catch (error) {
        console.error("Error fetching Care Rostering settings:", error);
        if (isMounted) {
          setAlertState({type: 'error', message: 'Failed to load current settings'});
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchCareRosteringInfo();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [selectedCustomer.clientId, selectedCustomer.environmentName]);

  return (
    <>
    {alert && (
      <ModuleAlert
        message={alert.message}
        type={alert.type as "success" | "error"}
        onClose={() => setAlertState(null)}
      />
    )}
    <Modal
      visible={visible}
      onCancelButtonClick={() => {
        handleClose();
      }}
      accent={ProjectAccents.PeoplePlanner}
      heading="Enable Care Rostering"
      onClose={() => handleClose()}
      primaryButtonProps={{
        text: "Save",
        onClick: handleSave,
        disabled: !isDirty || loading,
      }}
    >
      <div className={styles.modalContent}>
        {loading ? (
          <Spinner size="medium" />
        ) : (
          <Tooltip
            className={styles.tooltip}
            tooltipFor={
              <div className={styles.checkboxContainer}>
                <Checkbox
                  device="touch"
                  checked={isCareRosteringEnabled}
                  onChange={(value) => {
                    setIsCareRosteringEnabled(value.checked);
                    setIsDirty(true);
                  }}
                  disabled={loading}
                />
                <span className={styles.checkboxLabel}>
                  Enable Care Rostering
                </span>
              </div>
            }
          >
            {ENABLE_CARE_ROSTERING_TOOLTIP_TEXT}
          </Tooltip>
        )}
      </div>
    </Modal>
    </>
  );
};
